import { Col, Row } from 'antd';
import React from 'react';

import { momentFormat } from '../../../utils/common';
import { formatCurrencyToCompanyLocale } from '../../../utils/numbers';
import {
  sortColumnByDateString,
  sortColumnByMomentTime,
  sortColumnByNumber,
  sortColumnByStringField,
} from '../../../utils/tables';
import { CustomTable } from '../..';
import Text from '../../Text';
import LinkText from '../../Text/LinkText';

/**
 * @typedef {Object} PayrollFile
 * @property {string} fileName - Name of the payroll file
 * @property {string} downloadToken - Token used for downloading the file
 * @property {string} created - Date and time when file was created/downloaded
 * @property {string} downloadedBy - Name of user who downloaded the file
 */

/**
 * Renders a Table with a list of Payroll files
 *
 * @param {import('antd').TableProps & {
 *   t: Function,
 *   onDownload?: (downloadToken: string) => void,
 *   onViewDownloadHistory?: () => void,
 *   dataSource: PayrollFile[]
 * }} props Component props
 * @returns {JSX.Element} PayrollFileTable component
 */
const PayrollFileTable = props => {
  const { t, dataSource, loading, onDownload, onViewDownloadHistory, ...rest } = props;

  const COLUMNS = [
    {
      title: t('fileName'),
      dataIndex: 'fileName',
      key: 'filename',
      ...sortColumnByStringField('fileName'),
      render: (actions, data) => {
        if (typeof onDownload === 'function') {
          return (
            <LinkText variant="strong" onClick={() => onDownload(data.downloadToken)}>
              {data.fileName || '-'}
            </LinkText>
          );
        }

        return <Text>{data.fileName || '-'}</Text>;
      },
    },
    {
      defaultSortOrder: 'descend',
      title: t('dateDownloaded'),
      dataIndex: 'created',
      key: 'date',
      ...sortColumnByDateString('created'),
      render: date => momentFormat(new Date(date), 'MM-DD-YYYY'),
    },
    {
      defaultSortOrder: 'descend',
      title: t('timeDownloaded'),
      key: 'timeDownloaded',
      dataIndex: 'created',
      ...sortColumnByMomentTime('created', 'h:mm a'),
      render: date => momentFormat(new Date(date), 'h:mm a'),
    },
    {
      title: t('downloadedBy'),
      dataIndex: 'Downloaded By',
      key: 'downloadedBy',
      ...sortColumnByStringField('downloadedBy'),
      render: (actions, data) => {
        return (
          <Row gutter={8} align="middle">
            <Col>
              <Text variant="strong" size="sm">
                {data.downloadedBy}
              </Text>
            </Col>
            {data.downloadHistory.length > 0 && (
              <Col>
                <LinkText size="sm" onClick={() => onViewDownloadHistory(data.downloadHistory)}>
                  {t('viewHistory')}
                </LinkText>
              </Col>
            )}
          </Row>
        );
      },
    },
    {
      align: 'right',
      title: t('numberOfUsers'),
      dataIndex: 'countOfUsers',
      key: 'user',
      ...sortColumnByNumber('countOfUsers'),
    },
    {
      align: 'right',
      title: t('totalAmount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      ...sortColumnByNumber('totalAmount'),
      render: totalAmount => {
        return formatCurrencyToCompanyLocale(totalAmount);
      },
    },
  ];

  return (
    <CustomTable
      loading={loading}
      showSearchInput={false}
      columns={COLUMNS}
      dataSource={dataSource}
      {...rest}
    />
  );
};

export default PayrollFileTable;
